import { useState } from 'react'
import { Columns3, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import type { ColumnVisibilityToggleProps } from './types'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_PREFIX = 'felton-table-columns-'

// ---------------------------------------------------------------------------
// Helpers (pure functions)
// ---------------------------------------------------------------------------

function getColumnLabel(columnId: string, headerValue: unknown): string {
  if (typeof headerValue === 'string') {
    return headerValue
  }
  // Fallback: capitalize column ID
  return columnId.charAt(0).toUpperCase() + columnId.slice(1)
}

function buildVisibilitySnapshot(
  allColumns: ReturnType<ColumnVisibilityToggleProps['table']['getAllLeafColumns']>
): Record<string, boolean> {
  const snapshot: Record<string, boolean> = {}
  for (const col of allColumns) {
    snapshot[col.id] = col.getIsVisible()
  }
  return snapshot
}

function persistToStorage(
  tableId: string | undefined,
  visibility: Record<string, boolean>
): void {
  if (!tableId) {
    return
  }
  localStorage.setItem(
    `${STORAGE_PREFIX}${tableId}`,
    JSON.stringify(visibility)
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ColumnVisibilityToggle({
  table,
  tableId,
  excludeColumns,
}: ColumnVisibilityToggleProps) {
  const [searchQuery, setSearchQuery] = useState('')

  const excludeSet = excludeColumns && excludeColumns.length > 0
    ? new Set(excludeColumns)
    : null

  const allColumns = table.getAllLeafColumns().filter(
    (col) => !excludeSet || !excludeSet.has(col.id)
  )
  const visibleCount = table.getVisibleLeafColumns().filter(
    (col) => !excludeSet || !excludeSet.has(col.id)
  ).length
  const totalCount = allColumns.length

  // Filter columns based on search
  const filteredColumns = allColumns.filter((column) => {
    if (!searchQuery.trim()) return true
    const label = getColumnLabel(column.id, column.columnDef.header)
    return label.toLowerCase().includes(searchQuery.toLowerCase())
  })

  const handleToggleColumn = (columnId: string) => {
    const column = table.getColumn(columnId)
    if (!column) {
      return
    }
    column.toggleVisibility()

    // Persist after toggle -- need to compute next state
    // The toggle hasn't applied yet in this render, so compute it
    const currentVisibility = buildVisibilitySnapshot(allColumns)
    const nextVisibility = {
      ...currentVisibility,
      [columnId]: !currentVisibility[columnId],
    }
    persistToStorage(tableId, nextVisibility)
  }

  const handleShowAll = () => {
    table.toggleAllColumnsVisible(true)
    const visibility: Record<string, boolean> = {}
    for (const col of allColumns) {
      visibility[col.id] = true
    }
    persistToStorage(tableId, visibility)
  }

  const handleHideAll = () => {
    table.toggleAllColumnsVisible(false)
    const visibility: Record<string, boolean> = {}
    for (const col of allColumns) {
      visibility[col.id] = false
    }
    persistToStorage(tableId, visibility)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label="Columns"
          className="text-v11-text-primary border-v11-border bg-v11-surface hover:bg-v11-surface-hover"
        >
          <Columns3 className="h-4 w-4" />
          <span className="hidden sm:inline">Columns</span>
          <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs bg-blue-500/20 text-blue-400 border-blue-500/30">
            {visibleCount}/{totalCount}
          </Badge>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 bg-v11-surface border-v11-border">
        <DropdownMenuLabel className="text-v11-text-primary">Toggle columns</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-v11-border" />

        {/* Search Input */}
        <div className="px-2 pb-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-v11-text-muted" />
            <Input
              type="text"
              placeholder="Search columns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 pl-8 text-xs bg-v11-bg-secondary border-v11-border"
            />
          </div>
          {searchQuery && (
            <p className="text-xs text-v11-text-muted mt-1">
              {filteredColumns.length} of {totalCount} columns
            </p>
          )}
        </div>

        <DropdownMenuSeparator className="bg-v11-border" />

        {/* Scrollable Column List */}
        <div className="max-h-[300px] overflow-y-auto">
          {filteredColumns.map((column) => {
            const label = getColumnLabel(
              column.id,
              column.columnDef.header
            )
            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                checked={column.getIsVisible()}
                onCheckedChange={() => handleToggleColumn(column.id)}
                onSelect={(e) => e.preventDefault()}
              >
                {label}
              </DropdownMenuCheckboxItem>
            )
          })}

        {/* No results message */}
        {filteredColumns.length === 0 && (
          <div className="px-2 py-4 text-center text-xs text-v11-text-muted">
            No columns found
          </div>
        )}
        </div>

        <DropdownMenuSeparator className="bg-v11-border" />
        <DropdownMenuItem onSelect={handleShowAll}>
          Show All
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleHideAll}>
          Hide All
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
